import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CallEndIcon from '@mui/icons-material/CallEnd';
import { ClickAwayListener, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import React, { useEffect, useRef, useState } from 'react';
import AxiosConfig from '../../../../config/AxiosConfig';
import { jssipuaIsRegistered } from '../../../helpers/utils/jsSipUserAgent';
import usePhonecall from '../../../hooks/phone/usePhonecall';
import PhonecallIcon from '../../utils/common/PhonecallIcon';
import PhoneDialerPopper from './PhoneDialerPopper.jsx';

const PhoneSplitButton = ({
  isTicketExpanded,
  countryCode2,
  phoneList,
  handleStartPhoneCall,
  phoneCallId,
  session,
  handleEndPhoneCall,
}) => {
  const anchorRef = useRef(null);
  const theme = useTheme();
  const [options, setOptions] = useState(
    phoneList?.length ? [...phoneList, 'Nouveau N°'] : ['Nouveau N°']
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showPhoneList, setShowPhoneList] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [openConsentDialog, setOpenConsentDialog] = useState(false);
  const [recordingConsent, setRecordingConsent] = useState(null);

  const { data: phonecall } = usePhonecall(phoneCallId);

  useEffect(() => {
    const baseOptions = phoneList?.length
      ? [...phoneList, 'Nouveau N°']
      : ['Nouveau N°'];
    setOptions(baseOptions);
  }, [phoneList]);

  useEffect(() => {
    // Show dialog only if it's a new phone call and consent is not already given
    if (
      phoneCallId &&
      recordingConsent === null &&
      phonecall?.status === 'En cours'
    ) {
      setOpenConsentDialog(true);
    } else {
      setOpenConsentDialog(false);
    }
  }, [phoneCallId, recordingConsent, phonecall]);

  const handleConsentResponse = async (consent) => {
    try {
      setOpenConsentDialog(false);
      if (consent) {
        setRecordingConsent(true);
      } else {
        setRecordingConsent(false);
      }
      if (phoneCallId) {
        await AxiosConfig.create(
          'put',
          `/transcripts/${phoneCallId}/consent`
        ).call({
          recordingConsent: consent,
        });
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du consentement:', error);
      setRecordingConsent(null);
    }
  };

  const handleSelectedPhoneClick = () => {
    if (options[selectedIndex] === 'Nouveau N°') {
      setShowKeypad(true);
    } else {
      handleStartPhoneCall(options[selectedIndex]);
    }
  };

  const handleKeypadSubmit = (_e) => {
    handleStartPhoneCall(phoneNumber);
    setShowKeypad(true);
  };

  const handlePhoneListMenuItemClick = (e, index) => {
    setSelectedIndex(index);
    setShowPhoneList(false);
    if (options[index] === 'Nouveau N°') {
      setShowKeypad(true);
    }
  };

  const handleSelectedPhoneList = (_e) => {
    setShowPhoneList((state) => !state);
  };

  const handleSelectedPhoneListClose = (_e) => {
    setShowPhoneList(false);
  };

  const handleKeypadClose = (_e) => {
    setShowKeypad(false);
  };

  return (
    <>
      {session ? (
        <>
          <Button
            variant="contained"
            color="error"
            onClick={handleEndPhoneCall}
            size="small"
            endIcon={<CallEndIcon />}
          >
            Raccrocher
          </Button>
        </>
      ) : (
        <>
          {' '}
          <Box
            ref={anchorRef}
            sx={{
              display: 'inline-flex',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Button
              onClick={handleSelectedPhoneClick}
              sx={{
                background: theme.palette.background.phonecallSplitButton,
                borderRadius: '4px 0 0 4px',
              }}
              startIcon={<PhonecallIcon boxSize={24} iconSize={16} />}
              disabled={!jssipuaIsRegistered() || !isTicketExpanded}
            >
              {options[selectedIndex]}
            </Button>
            <Button
              size="small"
              onClick={handleSelectedPhoneList}
              sx={{
                background: theme.palette.background.phonecallSplitButton,
                borderRadius: '0 4px 4px 0',
                minWidth: '32px',
                px: 1,
              }}
              disabled={!jssipuaIsRegistered() || !isTicketExpanded}
            >
              <ArrowDropDownIcon />
            </Button>
          </Box>
          <Popper
            sx={{ zIndex: 1 }}
            open={showKeypad}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper>
                  <PhoneDialerPopper
                    countryCode2={countryCode2}
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                    onClose={handleKeypadClose}
                    onSubmit={handleKeypadSubmit}
                  />
                </Paper>
              </Grow>
            )}
          </Popper>
          <Popper
            sx={{ zIndex: 1 }}
            open={showPhoneList}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleSelectedPhoneListClose}>
                    <MenuList id="split-button-menu" autoFocusItem>
                      {options?.map((option, index) => (
                        <MenuItem
                          key={option}
                          selected={index === selectedIndex}
                          onClick={(event) =>
                            handlePhoneListMenuItemClick(event, index)
                          }
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </>
      )}

      <Dialog
        open={openConsentDialog}
        onClose={() => handleConsentResponse(false)}
      >
        <DialogTitle>
          Le client consent-il à être enregistré au cours de cet appel?
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => handleConsentResponse(false)}>Non</Button>
          <Button onClick={() => handleConsentResponse(true)} autoFocus>
            Oui
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PhoneSplitButton;
